/**
 * Unified Dashboard Design System
 * Cohesive color palette and design tokens for the admin dashboard
 */

export const dashboardTheme = {
  // Primary Section Colors
  sections: {
    activity: {
      primary: '#2563EB',
      light: '#3B82F6',
      lighter: '#60A5FA',
      lightest: '#DBEAFE',
      gradient: 'linear-gradient(135deg, #2563EB 0%, #3B82F6 50%, #60A5FA 100%)',
      glow: 'rgba(37, 99, 235, 0.3)',
    },
    team: {
      primary: '#10B981',
      light: '#34D399',
      lighter: '#6EE7B7',
      lightest: '#D1FAE5',
      gradient: 'linear-gradient(135deg, #10B981 0%, #34D399 50%, #6EE7B7 100%)',
      glow: 'rgba(16, 185, 129, 0.3)',
    },
    inventory: {
      primary: '#F59E0B',
      light: '#FBBF24',
      lighter: '#FCD34D',
      lightest: '#FEF3C7',
      gradient: 'linear-gradient(135deg, #F59E0B 0%, #FBBF24 50%, #FCD34D 100%)',
      glow: 'rgba(245, 158, 11, 0.3)',
    },
    analytics: {
      primary: '#8B5CF6',
      light: '#A78BFA',
      lighter: '#C4B5FD',
      lightest: '#EDE9FE',
      gradient: 'linear-gradient(135deg, #8B5CF6 0%, #A78BFA 50%, #C4B5FD 100%)',
      glow: 'rgba(139, 92, 246, 0.3)',
    },
  },

  // Neutral Colors
  neutral: {
    50: '#F8FAFC',
    100: '#F1F5F9',
    200: '#E2E8F0',
    300: '#CBD5E1',
    400: '#94A3B8',
    500: '#64748B',
    600: '#475569',
    700: '#334155',
    800: '#1E293B',
    900: '#0F172A',
  },

  // Semantic Colors
  semantic: {
    success: {
      primary: '#10B981',
      light: '#D1FAE5',
      dark: '#065F46',
    },
    warning: {
      primary: '#F59E0B',
      light: '#FEF3C7',
      dark: '#92400E',
    },
    error: {
      primary: '#EF4444',
      light: '#FEE2E2',
      dark: '#991B1B',
    },
    info: {
      primary: '#3B82F6',
      light: '#DBEAFE',
      dark: '#1E40AF',
    },
  },

  // Shadows
  shadows: {
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    '2xl': '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    glow: {
      blue: '0 0 20px rgba(37, 99, 235, 0.4)',
      emerald: '0 0 20px rgba(16, 185, 129, 0.4)',
      amber: '0 0 20px rgba(245, 158, 11, 0.4)',
      purple: '0 0 20px rgba(139, 92, 246, 0.4)',
    },
  },

  // Border Radius
  radius: {
    sm: '0.375rem',
    md: '0.5rem',
    lg: '0.75rem',
    xl: '1rem',
    '2xl': '1.5rem',
    full: '9999px',
  },

  // Transitions
  transitions: {
    fast: '150ms cubic-bezier(0.4, 0, 0.2, 1)',
    normal: '300ms cubic-bezier(0.4, 0, 0.2, 1)',
    slow: '500ms cubic-bezier(0.4, 0, 0.2, 1)',
    bounce: '600ms cubic-bezier(0.68, -0.55, 0.265, 1.55)',
  },

  // Typography
  typography: {
    fontFamily: {
      sans: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      mono: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
    },
    fontSize: {
      xs: '0.75rem',
      sm: '0.875rem',
      base: '1rem',
      lg: '1.125rem',
      xl: '1.25rem',
      '2xl': '1.5rem',
      '3xl': '1.875rem',
      '4xl': '2.25rem',
      '5xl': '3rem',
    },
    fontWeight: {
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
      extrabold: 800,
    },
  },

  // Z-Index
  zIndex: {
    base: 0,
    dropdown: 1000,
    sticky: 1020,
    fixed: 1030,
    modalBackdrop: 1040,
    modal: 1050,
    popover: 1060,
    tooltip: 1070,
  },
};

export type DashboardTheme = typeof dashboardTheme;
export type SectionKey = keyof typeof dashboardTheme.sections;

